/**
 * A/B Testing Utility
 * 
 * Uses cookies to persistently assign users to test buckets.
 * Works with Vercel Analytics to track which variant users see.
 */

import { track } from '@vercel/analytics';

const COOKIE_NAME = 'ab-test-bucket';
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Get or create a test bucket assignment for a user
 * @param {string} testName - Name of the A/B test (e.g., 'product-vs-system')
 * @param {number} ratio - Percentage of traffic to variant B (0.0 = all A, 0.5 = 50/50, 1.0 = all B)
 * @returns {{ bucket: 'A' | 'B', isNew: boolean }}
 */
export function getTestBucket(testName, ratio = 0.5) {
  const cookieKey = `${COOKIE_NAME}-${testName}`;
  
  // Check for existing assignment
  const existingBucket = getCookie(cookieKey);
  if (existingBucket === 'A' || existingBucket === 'B') {
    return { bucket: existingBucket, isNew: false };
  }
  
  // Assign new bucket based on ratio
  // ratio represents % to B (variant), so ratio = 0 means all A, ratio = 1.0 means all B
  const bucket = Math.random() < ratio ? 'B' : 'A';
  setCookie(cookieKey, bucket, COOKIE_EXPIRY_DAYS);
  
  return { bucket, isNew: true };
}

/**
 * Force a specific bucket (useful for testing/preview)
 * @param {string} testName - Name of the A/B test
 * @param {'A' | 'B'} bucket - Bucket to assign
 */
export function forceTestBucket(testName, bucket) {
  const cookieKey = `${COOKIE_NAME}-${testName}`;
  setCookie(cookieKey, bucket, COOKIE_EXPIRY_DAYS);
}

/**
 * Clear test bucket assignment
 * @param {string} testName - Name of the A/B test
 */
export function clearTestBucket(testName) {
  const cookieKey = `${COOKIE_NAME}-${testName}`;
  deleteCookie(cookieKey);
}

// Cookie helpers
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

function setCookie(name, value, days) {
  if (typeof document === 'undefined') return;
  
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Lax`;
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// URL parameter override (for testing)
export function checkUrlOverride(testName) {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  const override = params.get(`ab-${testName}`);
  
  if (override === 'A' || override === 'B') {
    forceTestBucket(testName, override);
    return override;
  }
  
  return null;
}

/**
 * Capture session data for analytics
 * @returns {Object} Session data including UTM params, referrer, etc.
 */
export function captureSessionData() {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  
  return {
    referrer: document.referrer || 'direct',
    utmSource: params.get('utm_source') || null,
    utmMedium: params.get('utm_medium') || null,
    utmCampaign: params.get('utm_campaign') || null,
    utmTerm: params.get('utm_term') || null,
    utmContent: params.get('utm_content') || null,
    path: window.location.pathname,
    search: window.location.search,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent,
    timestamp: Date.now()
  };
}

/**
 * Get all active bucket assignments for user
 * @returns {Object} Map of experiment names to bucket assignments
 */
export function getAllBuckets() {
  if (typeof document === 'undefined') return {};
  
  const cookies = document.cookie.split(';');
  const buckets = {};
  
  cookies.forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name.startsWith(COOKIE_NAME)) {
      const experimentName = name.replace(`${COOKIE_NAME}-`, '');
      buckets[experimentName] = value;
    }
  });
  
  return buckets;
}

/**
 * Clear all experiment bucket assignments
 */
export function clearAllBuckets() {
  const buckets = getAllBuckets();
  Object.keys(buckets).forEach(experimentName => {
    clearTestBucket(experimentName);
  });
}

